import Clock from "../Core/Clock.js";
import defined from "../Core/defined.js";
import DeveloperError from "../Core/DeveloperError.js";
import Event from "../Core/Event.js";
import JulianDate from "../Core/JulianDate.js";
import createRawPropertyDescriptor from "./createRawPropertyDescriptor.js";

/**
 * Represents desired clock settings for a particular {@link DataSource}.  These settings may be applied
 * to the {@link Clock} when the DataSource is loaded.
 *
 * @alias DataSourceClock
 * @constructor
 */
function DataSourceClock() {
  this._definitionChanged = new Event();
  this._startTime = undefined;
  this._stopTime = undefined;
  this._currentTime = undefined;
  this._clockRange = undefined;
  this._clockStep = undefined;
  this._multiplier = undefined;
}
Object.defineProperties(DataSourceClock.prototype, {
  /**
   * Gets the event that is raised whenever a new property is assigned.
   * @memberof DataSourceClock.prototype
   *
   * @type {Event}
   * @readonly
   */
  definitionChanged: {
    get: function () {
      return this._definitionChanged;
    }
  },
  /**
   * Gets or sets the desired start time of the clock.
   * See {@link Clock#startTime}.
   * @memberof DataSourceClock.prototype
   * @type {JulianDate}
   */
  startTime: createRawPropertyDescriptor("startTime"),
  /**
   * Gets or sets the desired stop time of the clock.
   * See {@link Clock#stopTime}.
   * @memberof DataSourceClock.prototype
   * @type {JulianDate}
   */
  stopTime: createRawPropertyDescriptor("stopTime"),
  /**
   * Gets or sets the desired current time when this data source is loaded.
   * See {@link Clock#currentTime}.
   * @memberof DataSourceClock.prototype
   * @type {JulianDate}
   */
  currentTime: createRawPropertyDescriptor("currentTime"),
  /**
   * Gets or sets the desired clock range setting.
   * See {@link Clock#clockRange}.
   * @memberof DataSourceClock.prototype
   * @type {ClockRange}
   */
  clockRange: createRawPropertyDescriptor("clockRange"),
  /**
   * Gets or sets the desired clock step setting.
   * See {@link Clock#clockStep}.
   * @memberof DataSourceClock.prototype
   * @type {ClockStep}
   */
  clockStep: createRawPropertyDescriptor("clockStep"),
  /**
   * Gets or sets the desired clock multiplier.
   * See {@link Clock#multiplier}.
   * @memberof DataSourceClock.prototype
   * @type {number}
   */
  multiplier: createRawPropertyDescriptor("multiplier")
});

/**
 * Duplicates a DataSourceClock instance.
 *
 * @param {DataSourceClock} [result] The object onto which to store the result.
 * @returns {DataSourceClock} The modified result parameter or a new instance if one was not provided.
 */
DataSourceClock.prototype.clone = function (result) {
  if (!defined(result)) {
    result = new DataSourceClock();
  }
  result.startTime = this.startTime;
  result.stopTime = this.stopTime;
  result.currentTime = this.currentTime;
  result.clockRange = this.clockRange;
  result.clockStep = this.clockStep;
  result.multiplier = this.multiplier;
  return result;
};

/**
 * Returns true if this DataSourceClock is equivalent to the other
 *
 * @param {DataSourceClock} [other] The other DataSourceClock to compare to.
 * @returns {boolean} <code>true</code> if the DataSourceClocks are equal; otherwise, <code>false</code>.
 */
DataSourceClock.prototype.equals = function (other) {
  return this === other || defined(other) && JulianDate.equals(this.startTime, other.startTime) && JulianDate.equals(this.stopTime, other.stopTime) && JulianDate.equals(this.currentTime, other.currentTime) && this.clockRange === other.clockRange && this.clockStep === other.clockStep && this.multiplier === other.multiplier;
};

/**
 * Assigns each unassigned property on this object to the value
 * of the same property on the provided source object.
 *
 * @param {DataSourceClock} source The object to be merged into this object.
 */
DataSourceClock.prototype.merge = function (source) {
  var _this$startTime, _this$stopTime, _this$currentTime, _this$clockRange, _this$clockStep, _this$multiplier;
  this.startTime = (_this$startTime = this.startTime) !== null && _this$startTime !== void 0 ? _this$startTime : source.startTime;
  this.stopTime = (_this$stopTime = this.stopTime) !== null && _this$stopTime !== void 0 ? _this$stopTime : source.stopTime;
  this.currentTime = (_this$currentTime = this.currentTime) !== null && _this$currentTime !== void 0 ? _this$currentTime : source.currentTime;
  this.clockRange = (_this$clockRange = this.clockRange) !== null && _this$clockRange !== void 0 ? _this$clockRange : source.clockRange;
  this.clockStep = (_this$clockStep = this.clockStep) !== null && _this$clockStep !== void 0 ? _this$clockStep : source.clockStep;
  this.multiplier = (_this$multiplier = this.multiplier) !== null && _this$multiplier !== void 0 ? _this$multiplier : source.multiplier;
};

/**
 * Gets the value of this clock instance as a {@link Clock} object.
 *
 * @returns {Clock} The modified result parameter or a new instance if one was not provided.
 */
DataSourceClock.prototype.getValue = function (result) {
  var _this$startTime2, _this$stopTime2, _this$currentTime2, _this$clockRange2, _this$multiplier2, _this$clockStep2;
  if (!defined(result)) {
    result = new Clock();
  }
  result.startTime = (_this$startTime2 = this.startTime) !== null && _this$startTime2 !== void 0 ? _this$startTime2 : result.startTime;
  result.stopTime = (_this$stopTime2 = this.stopTime) !== null && _this$stopTime2 !== void 0 ? _this$stopTime2 : result.stopTime;
  result.currentTime = (_this$currentTime2 = this.currentTime) !== null && _this$currentTime2 !== void 0 ? _this$currentTime2 : result.currentTime;
  result.clockRange = (_this$clockRange2 = this.clockRange) !== null && _this$clockRange2 !== void 0 ? _this$clockRange2 : result.clockRange;
  result.multiplier = (_this$multiplier2 = this.multiplier) !== null && _this$multiplier2 !== void 0 ? _this$multiplier2 : result.multiplier;
  result.clockStep = (_this$clockStep2 = this.clockStep) !== null && _this$clockStep2 !== void 0 ? _this$clockStep2 : result.clockStep;
  return result;
};
export default DataSourceClock;